import { useEffect, useRef, useState } from "react"
import * as S from "./styled"
import { Dialog, DialogContent } from "@mui/material"
import { formatPhone } from "../../../utils/masks/phone"
import { isPhoneNumberValid } from "../../../utils/tb/phone/isValidPhone"

import { ReactComponent as WhatsappIcon } from "../../../assets/icons/whatsapp.svg"

type Props = {
  shown: boolean
  basePhone: string
  handleConfirm: (phone: string) => void
  handleClose: () => void
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18" strokeLinecap="round" />
    <path d="m6 6 12 12" strokeLinecap="round" />
  </svg>
)

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const digitsOf = (phone: string) => String(phone ?? "").replace(/\D/g, "")

/** Celular brasileiro com DDD: 11 dígitos, 9 depois do DDD. */
export const isValidCellPhone = (phone: string) => {
  const digits = digitsOf(phone)
  return digits.length === 11 && digits[2] === "9" && isPhoneNumberValid(phone, true)
}

/** Máscara progressiva: "48996710304" → "(48) 99671-0304". */
export const displayPhone = (phone: string) => {
  const d = digitsOf(phone).slice(0, 11)
  if (!d) return ""
  if (d.length <= 2) return `(${d}`
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/**
 * Confirmação do WhatsApp antes do pagamento: o PDF dos ingressos é enviado
 * para este número logo depois da compra. Mostra o número em destaque para
 * confirmar com um toque; "Corrigir número" abre a edição.
 */
const ConfirmPhoneNumberModal = ({
  shown,
  basePhone,
  handleConfirm,
  handleClose,
}: Props) => {
  const [phone, setPhone] = useState(formatPhone(basePhone))
  const [editing, setEditing] = useState(!isValidCellPhone(basePhone))
  const [touched, setTouched] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const valid = isValidCellPhone(phone)
  const showError = editing && touched && !valid

  useEffect(() => {
    if (!shown) return
    setPhone(formatPhone(basePhone))
    setEditing(!isValidCellPhone(basePhone))
    setTouched(false)
  }, [shown, basePhone])

  useEffect(() => {
    if (shown && editing) {
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [shown, editing])

  const confirm = () => {
    if (!valid) {
      setTouched(true)
      return
    }
    handleConfirm(formatPhone(digitsOf(phone)))
  }

  return (
    <Dialog
      open={shown}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="confirm-whatsapp-title"
      PaperProps={{
        style: {
          // Margem menor que a padrão do MUI (32px): no celular sobra largura
          // para o número e o modal fica centralizado.
          margin: 16,
          width: "calc(100% - 32px)",
          maxWidth: 400,
          borderRadius: 24,
          position: "relative",
          boxShadow: "0 24px 64px rgba(15, 23, 42, 0.22)",
        },
      }}
    >
      <S.CloseButton type="button" onClick={handleClose} aria-label="Fechar">
        <CloseIcon />
      </S.CloseButton>

      <DialogContent style={{ padding: "28px 20px 18px" }}>
        {!editing ? (
          <S.Body key="confirm">
            <S.Header>
              <S.IconBadge>
                <WhatsappIcon aria-hidden="true" />
              </S.IconBadge>
              <S.Title id="confirm-whatsapp-title">
                Seus ingressos vão para este WhatsApp?
              </S.Title>
            </S.Header>

            <S.PhoneCard>
              <S.PhoneCardIcon>
                <WhatsappIcon aria-hidden="true" />
              </S.PhoneCardIcon>
              <S.PhoneCardText>
                <S.PhoneCardLabel>WhatsApp</S.PhoneCardLabel>
                <S.PhoneNumber data-testid="confirm-phone-number">
                  {displayPhone(phone)}
                </S.PhoneNumber>
              </S.PhoneCardText>
            </S.PhoneCard>

            <S.Actions>
              <S.Button type="button" onClick={confirm} autoFocus>
                Sim, é meu WhatsApp
                <ArrowIcon />
              </S.Button>
              <S.LinkButton type="button" onClick={() => setEditing(true)}>
                Corrigir número
              </S.LinkButton>
            </S.Actions>
          </S.Body>
        ) : (
          <S.BodyForm
            key="edit"
            onSubmit={(e) => {
              e.preventDefault()
              confirm()
            }}
          >
            <S.Header>
              <S.IconBadge>
                <WhatsappIcon aria-hidden="true" />
              </S.IconBadge>
              <S.Title id="confirm-whatsapp-title">
                Qual é o seu WhatsApp?
              </S.Title>
              <S.Subtitle>
                Logo após o pagamento, enviamos o PDF com seus ingressos para
                esse número.
              </S.Subtitle>
            </S.Header>

            <S.Field>
              <S.Label htmlFor="confirm-whatsapp-phone">
                Celular com DDD
              </S.Label>
              <S.InputWrapper $invalid={showError}>
                <WhatsappIcon aria-hidden="true" />
                <S.PhoneInput
                  ref={inputRef}
                  id="confirm-whatsapp-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="(00) 00000-0000"
                  aria-invalid={showError}
                  aria-describedby={showError ? "confirm-whatsapp-error" : undefined}
                  value={displayPhone(phone)}
                  onChange={({ target }) =>
                    setPhone(formatPhone(digitsOf(target.value).slice(0, 11)))
                  }
                  onBlur={() => setTouched(true)}
                />
              </S.InputWrapper>
              {showError && (
                <S.ErrorText id="confirm-whatsapp-error" role="alert">
                  Digite um celular com DDD, por exemplo (48) 99999-0000.
                </S.ErrorText>
              )}
            </S.Field>

            <S.Actions>
              <S.Button type="submit" $disabled={!valid} aria-disabled={!valid}>
                Confirmar e ir para o pagamento
                <ArrowIcon />
              </S.Button>
              {isValidCellPhone(basePhone) && (
                <S.LinkButton
                  type="button"
                  onClick={() => {
                    setPhone(formatPhone(basePhone))
                    setTouched(false)
                    setEditing(false)
                  }}
                >
                  Voltar
                </S.LinkButton>
              )}
            </S.Actions>
          </S.BodyForm>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmPhoneNumberModal
